import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { value: 'America/Denver',      label: 'Mountain (Denver)' },
  { value: 'America/Chicago',     label: 'Central (Chicago)' },
  { value: 'America/New_York',    label: 'Eastern (New York)' },
  { value: 'America/Anchorage',   label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (Honolulu)' },
];

export default function Settings() {
  const { user, setUser } = useAuth();
  const [push, setPush] = useState(user?.push_enabled);
  const [sms, setSms] = useState(user?.sms_enabled);
  const [email, setEmail] = useState(user?.email_enabled);
  const [phone, setPhone] = useState(user?.phone || '');
  const [smsConsent, setSmsConsent] = useState(!!user?.sms_consent_at);
  const [timezone, setTimezone] = useState(user?.timezone || 'America/Los_Angeles');
  const [defaultDealershipId, setDefaultDealershipId] = useState(user?.default_dealership_id || '');
  const [defaultBranchId, setDefaultBranchId] = useState(user?.default_branch_id || '');
  const [hideSnooze, setHideSnoozeState] = useState(() => localStorage.getItem('utsales:hide_snooze_btn') === 'true');
  const setHideSnooze = (v) => {
    setHideSnoozeState(v);
    localStorage.setItem('utsales:hide_snooze_btn', String(v));
  };
  const [pushSubStatus, setPushSubStatus] = useState('idle');

  const isSuper = user?.role === 'superadmin';

  const dealerships = useQuery({
    queryKey: ['dealerships'],
    queryFn: () => api.get('/dealerships').then(r => r.data.data),
    enabled: isSuper,
  });
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get('/branches').then(r => r.data.data),
    enabled: isSuper,
  });
  const branchesForDealer = (branches.data || []).filter(
    b => !defaultDealershipId || String(b.dealership_id) === String(defaultDealershipId)
  );

  const save = useMutation({
    mutationFn: (data) => api.put(`/users/${user.id}/preferences`, data).then(r => r.data.data),
    onSuccess: (d) => setUser({ ...user, ...d }),
  });

  async function enableBrowserPush() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushSubStatus('not supported');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const keyResp = await api.get('/notifications/vapid-public-key');
      const publicKey = keyResp.data?.data?.key;
      if (!publicKey) { setPushSubStatus('VAPID not configured on server'); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await api.post('/notifications/push/subscribe', sub.toJSON());
      setPushSubStatus('subscribed');
    } catch (err) {
      setPushSubStatus(`failed: ${err.message}`);
    }
  }

  return (
    <div className="space-y-6 max-w-md">
      <header>
        <div className="section-eyebrow">Account</div>
        <h1 className="text-2xl">Settings</h1>
        <div className="rule-crimson" />
      </header>

      <section className="card p-4 space-y-3">
        <div className="section-eyebrow">Notifications</div>
        <Toggle label="In-app + email" value={email} onChange={setEmail} />
        <Toggle label="Browser push" value={push} onChange={setPush} />
        <Toggle label="SMS (last-resort fallback)" value={sms} onChange={setSms} />
        <div>
          <label className="label">Phone (E.164, e.g. +12095551234)</label>
          <input className="input" value={phone} onChange={e => setPhone(e.target.value)} />
        </div>

        {sms && (
          <div className="rounded border border-navy/20 bg-panel p-3 text-xs space-y-2">
            <div className="font-semibold text-navy">SMS Consent</div>
            <p>
              By providing your mobile number and checking the box below, you agree to receive
              text messages from ASE CRM related to your work
              account: customer activity alerts, weekly recap reminders, and escalation pings
              from ASE CRM. Messages may contain links to the app.
            </p>
            <p>
              Message frequency varies. Message and data rates may apply. Reply STOP to opt out
              at any time, or HELP for help. For help, contact your dealership admin.
            </p>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={e => setSmsConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-crimson"
              />
              <span>I agree to receive SMS messages at the number above on these terms.</span>
            </label>
          </div>
        )}

        <div className="flex justify-end">
          <button className="btn-primary"
                  disabled={save.isPending || (sms && (!phone || !smsConsent))}
                  onClick={() => save.mutate({
                    push_enabled: push,
                    sms_enabled: sms,
                    email_enabled: email,
                    phone,
                    sms_consent: sms ? smsConsent : false,
                  })}>
            Save
          </button>
        </div>
      </section>

      <section className="card p-4 space-y-3">
        <div className="section-eyebrow">Time zone</div>
        <p className="text-xs text-muted">Used for displaying dates and times throughout the app.</p>
        <select
          className="input"
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
        >
          {TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
        <div className="flex justify-end">
          <button className="btn-primary"
                  disabled={save.isPending || timezone === user?.timezone}
                  onClick={() => save.mutate({ timezone })}>
            Save
          </button>
        </div>
      </section>

      {isSuper && (
        <section className="card p-4 space-y-3">
          <div className="section-eyebrow">Default scope (superadmin)</div>
          <p className="text-xs text-muted">
            Superadmins don't belong to a single dealership. Pick the dealership/branch you want
            new notes, recaps, and other entries to default to. The customer's dealership always
            wins when a customer is attached to the entry.
          </p>
          <div>
            <label className="label">Default dealership</label>
            <select
              className="input"
              value={defaultDealershipId || ''}
              onChange={e => {
                setDefaultDealershipId(e.target.value);
                setDefaultBranchId(''); // reset branch when dealership changes
              }}
            >
              <option value="">— none —</option>
              {(dealerships.data || []).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Default branch</label>
            <select
              className="input"
              value={defaultBranchId || ''}
              onChange={e => setDefaultBranchId(e.target.value)}
              disabled={!defaultDealershipId}
            >
              <option value="">— none —</option>
              {branchesForDealer.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary"
                    disabled={save.isPending}
                    onClick={() => save.mutate({
                      default_dealership_id: defaultDealershipId ? parseInt(defaultDealershipId, 10) : null,
                      default_branch_id: defaultBranchId ? parseInt(defaultBranchId, 10) : null,
                    })}>
              Save
            </button>
          </div>
        </section>
      )}

      <section className="card p-4 space-y-2">
        <div className="section-eyebrow">Display</div>
        <Toggle label="Hide Snooze button on follow-ups" value={hideSnooze} onChange={setHideSnooze} />
        <p className="text-xs text-muted">Per-device preference. Re-enable here if you want it back.</p>
      </section>

      <section className="card p-4 space-y-2">
        <div className="section-eyebrow">Browser Push Subscription</div>
        <p className="text-sm text-muted">
          Enable push so you get notified when the app is closed.
          On iOS, "Add to Home Screen" first, then come back here.
        </p>
        <button className="btn-secondary" onClick={enableBrowserPush}>Enable on this device</button>
        <div className="text-xs text-muted">{pushSubStatus}</div>
      </section>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label className="flex items-center justify-between text-sm">
      <span>{label}</span>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
             className="w-5 h-5 accent-crimson" />
    </label>
  );
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
